import {useCallback, useEffect, useRef, useState} from "react";
import {EventEmitter} from "nate-react-api-helpers";

export type Item<T> = {
    value: T
    expiresAt: number; // e.g. Date.now()
}

const heartbeat = new EventEmitter<boolean>();

setInterval(() => {
    heartbeat.emit(true);
}, 60 * 1000);

// read a stored value outside react (see useLocalStorage for the hook form).
export function getLocalStorage<T>(key: string, defaultValue: T): T {
    return decodeItem(key, defaultValue);
}

// write a value outside react. A null expiresAt keeps the value for good.
export function setLocalStorage<T>(key: string, value: T, expiresAt: number | null = null) {
    const item: Item<T> = {value, expiresAt: expiresAt ?? Number.MAX_SAFE_INTEGER};
    localStorage.setItem(key, JSON.stringify(item));
}

function decodeItem<T>(key: string, defaultValue: T) {
    const value = localStorage.getItem(key);
    if(value === null) {
        return defaultValue;
    }

    const valueObj = JSON.parse(value) as Item<T>;

    if(!valueObj) {
        localStorage.removeItem(key);
        return defaultValue;
    }

    if(valueObj.expiresAt <= Date.now()) return defaultValue;

    return valueObj.value;
}

export function useLocalStorage<T>(key: string, defaultValue: T): [T, (input: Item<T>|null) => void] {

    const [value, setValue] = useState(decodeItem(key, defaultValue));

    const defaultValueRef = useRef(defaultValue);
    defaultValueRef.current = defaultValue;

    useEffect(() => {
        setValue(decodeItem(key, defaultValueRef.current))
    }, [key]);

    useEffect(() => {
        const sub = heartbeat.subscribe(() => {
            setValue(decodeItem(key, defaultValueRef.current));
        });

        return () => sub.cancel();
    }, [key]);

    const onChangeCb = (input: Item<T> | null) => {
        if(input === null) {
            localStorage.removeItem(key);
            setValue(defaultValueRef.current);
            return;
        }

        localStorage.setItem(key, JSON.stringify(input));
        setValue(input.value);
    }

    const onChangeRef = useRef(onChangeCb);
    onChangeRef.current = onChangeCb;

    const onChange = useCallback((value: Item<T> | null) => onChangeRef.current(value), [])

    return [value, onChange]
}